import React, { useState, useEffect } from "react";
import {
  Card, Form, Container, Row, Col,
  OverlayTrigger, Tooltip, Dropdown, Button,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import resetBtn from "../../../assets/images/reset-arrow.svg";
import { getAllLabs, labsDeleteById, labsUpdate } from "../../../actions/labsAction";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import FullPageLoader from "../../../components/FullPageLoader/FullPageLoader";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
import DataTable, { createTheme } from "react-data-table-component";

const PAYMENT_TYPES = ["Self Pay", "Insurance"];
const LAB_TYPES = ["At Home", "In Person"];
const LAB_CATEGORIES = ["STD Tests", "General Health", "Hormonal Tests", "Panels"];

const typeLabel = (t) => (t === "At Home" ? "At-Home Kit" : t);

createTheme(
  "solarized",
  {
    text: { primary: "#000", secondary: "#000" },
    background: { default: "#fff" },
    context: { background: "#cb4b16", text: "#FFFFFF" },
    divider: { default: "#DFE0EB" },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const Index = () => {
  const [labsData, setLabsData] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loader, setLoader] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin.role;
  const [getDelete, setDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  // Filter state
  const [filterText, setFilterText] = useState("");
  const [filterPaymentType, setFilterPaymentType] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const hideModel = () => setDelete(false);
  const showModel = (id) => { setDeleteId(id); setDelete(true); };

  const labsTypeStatus = async (id, status) => {
    let res = await labsUpdate(id, { status });
    if (res?.data?.success) {
      toast.success(res.data.message);
      fetchAllLabs();
    }
  };

  const deleteLabs = async () => {
    setDelete(false);
    setLoader(true);
    let res = await labsDeleteById(deleteId);
    if (res?.data?.success) {
      toast.success(res.data.message);
      fetchAllLabs();
    }
    setLoader(false);
  };

  const fetchAllLabs = async () => {
    setLoader(true);
    let res = await getAllLabs();
    setLoader(false);
    if (res?.data?.data) setLabsData(res.data.data);
  };

  useEffect(() => { fetchAllLabs(); }, []);

  // Apply all active filters whenever any filter or source data changes
  useEffect(() => {
    let result = [...labsData];
    if (filterText) {
      const lower = filterText.toLowerCase();
      result = result.filter(
        (d) =>
          d?.title?.toLowerCase().includes(lower) ||
          d?.slug?.toLowerCase().includes(lower) ||
          String(d?.price).includes(lower)
      );
    }
    if (filterPaymentType)
      result = result.filter((d) => (d?.paymentType || "Self Pay") === filterPaymentType);
    if (filterType) result = result.filter((d) => d?.type === filterType);
    if (filterCategory) result = result.filter((d) => d?.category === filterCategory);
    if (filterStatus !== "") result = result.filter((d) => String(d?.status) === filterStatus);
    setFilteredItems(result);
  }, [labsData, filterText, filterPaymentType, filterType, filterCategory, filterStatus]);

  const resetFilter = () => {
    setFilterText("");
    setFilterPaymentType("");
    setFilterType("");
    setFilterCategory("");
    setFilterStatus("");
  };

  const caseInsensitiveSort = (rowA, rowB) => {
    const a = rowA?.title?.toLowerCase() ?? "";
    const b = rowB?.title?.toLowerCase() ?? "";
    return a > b ? 1 : b > a ? -1 : 0;
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.title,
      sortable: true,
      sortFunction: caseInsensitiveSort,
    },
    {
      name: "Payment",
      selector: (row) => row?.paymentType || "Self Pay",
      sortable: true,
    },
    {
      name: "Service",
      selector: (row) => typeLabel(row?.type),
      sortable: true,
    },
    {
      name: "Category",
      selector: (row) => row?.category,
      sortable: true,
    },
    {
      name: "Price",
      selector: (row) => `$${row?.price ?? 0}`,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) =>
        row.status ? (
          <label className="label label-success m-0">Active</label>
        ) : (
          <label className="label label-danger m-0">Inactive</label>
        ),
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="td-actions table-actions-btns">
          <ul className="list-unstyled mb-0 d-flex justify-content-center">
            {/* Status toggle */}
            <li className="d-inline-block align-top">
              <Dropdown className="patient-status-btn" style={{ position: "initial" }}>
                <Dropdown.Toggle
                  variant=""
                  id="dropdown-basic"
                  disabled={!currentUserRole?.editLabs}
                >
                  {row?.status ? "Active" : "Inactive"}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    className={row.status ? "label-success text-white" : ""}
                    onClick={() => labsTypeStatus(row?._id, true)}
                  >
                    Active
                  </Dropdown.Item>
                  <Dropdown.Item
                    className={!row.status ? "label-danger text-white" : ""}
                    onClick={() => labsTypeStatus(row?._id, false)}
                  >
                    Inactive
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </li>

            {/* Edit */}
            <li className="d-inline-block align-top">
              <OverlayTrigger
                overlay={<Tooltip id="tooltip-labs-edit">Edit</Tooltip>}
                placement="left"
              >
                <Link
                  to={currentUserRole?.editLabs ? `/forms/labs/edit/${row._id}` : ""}
                  type="button"
                  className={`btn-link btn-icon btn btn-success${!currentUserRole?.editLabs ? " disabled" : ""}`}
                >
                  <FaEdit />
                </Link>
              </OverlayTrigger>
            </li>

            {/* Delete */}
            <li className="d-inline-block align-top">
              <OverlayTrigger
                overlay={<Tooltip id="tooltip-labs-delete">Delete</Tooltip>}
                placement="left"
              >
                <button
                  onClick={() => currentUserRole?.deleteLabs && showModel(row._id)}
                  type="button"
                  className={`btn-link btn-icon btn btn-danger${!currentUserRole?.deleteLabs ? " disabled" : ""}`}
                  disabled={!currentUserRole?.deleteLabs}
                >
                  <FaTrash />
                </button>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
      ),
      sortable: false,
    },
  ];

  return (
    <>
      {getDelete ? (
        <SweetAlert
          warning
          showCloseButton
          showCancel
          confirmBtnText="Yes"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="light"
          title="Are you sure you want to delete?"
          onConfirm={deleteLabs}
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          You will not be able to recover this data!
        </SweetAlert>
      ) : null}

      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3 precriptionfill-admin-tablelayout">
          <Container fluid>
            <Row className="medical-typeuser-wrap">
              <Col md="12">
                {/* Filter Card */}
                <Card className="filter-card card">
                  <Card.Header>
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">Filter</Card.Title>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col xl={3} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">Search</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Search by name..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xl={3} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">Payment Type</Form.Label>
                          <select
                            value={filterPaymentType}
                            onChange={(e) => setFilterPaymentType(e.target.value)}
                          >
                            <option value="">All Payment Types</option>
                            {PAYMENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </Form.Group>
                      </Col>
                      <Col xl={3} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">Service Type</Form.Label>
                          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                            <option value="">All Service Types</option>
                            {LAB_TYPES.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                          </select>
                        </Form.Group>
                      </Col>
                      <Col xl={3} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">Category</Form.Label>
                          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                            <option value="">All Categories</option>
                            {LAB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </Form.Group>
                      </Col>
                      <Col xl={3} sm={6} className="search-wrap">
                        <Form.Group>
                          <Form.Label className="d-block mb-2 text-white">Status</Form.Label>
                          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                            <option value="">All</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <button
                          type="button"
                          className="outline-button filter-reset-btn"
                          onClick={resetFilter}
                        >
                          <img src={resetBtn} className="img-fluid mr-3" alt="reset" />
                          Reset
                        </button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Table header */}
                <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                  <h4 className="card-title">Labs ({filteredItems.length})</h4>
                  {currentUserRole?.addLabs ? (
                    <div className="btn-add">
                      <Link
                        className="float-sm-right btn-filled d-flex align-items-center"
                        to="/forms/labs/add"
                      >
                        <span className="add-icon mr-2"><FaPlus /></span>
                        Add Lab Test
                      </Link>
                    </div>
                  ) : (
                    <div className="btn-add">
                      <Button className="float-sm-right btn-filled d-flex align-items-center disabled" disabled>
                        <span className="add-icon mr-2"><FaPlus /></span>
                        Add Lab Test
                      </Button>
                    </div>
                  )}
                </div>

                <div className="custom-datatable-pagination">
                  <DataTable
                    columns={columns}
                    data={filteredItems}
                    theme="solarized"
                    pagination
                    className="react-datatable"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default Index;
